import * as Sentry from "@sentry/react";
import React, { useState } from "react";

import { AdminContainer } from "../../../components/Admin";
import PageTitle from "../../../components/PageTitle";
import Spacer from "../../../components/Spacer";
import CompanySettingsSection from "./components/CompanySettingsSection";
import CustomBrandingSection from "./components/CustomBrandingSection";
import HeaderRow from "./components/HeaderRow";
import PerformYardFeaturesSection from "./components/PerformYardFeaturesSection";
import SeatStatCards from "./components/SeatStatCards";

import { useAdminSettingsHomeSeatUsage, useCompanySettings, useRbacPrimaryRolePermissions } from "../../../api/hooks";
import useScrollToTopOfPageOnMount from "../../../hooks/useScrollToTopOfPageOnMount";

import {
	adminSettingsHomeBrandingQuery,
	adminSettingsHomeFunctionsQuery,
	adminSettingsHomeSeatUsageQuery,
	adminSettingsHomeSettingsQuery,
	companySettingsQuery,
	currentUserQuery,
	rbacPermissionsQuery,
} from "../../../api/queries";
import { dataLoader } from "../../../api/queries/dataLoader";
import { queryClient } from "../../../api/queryClient";

import ErrorPage from "../../Error";

const readCompanyIdParam = (): string | undefined =>
	new URLSearchParams(window.location.search).get("companyId") ?? undefined;

export const adminSettingsHomeLoader = async () => {
	const companyId = readCompanyIdParam();
	await dataLoader([[currentUserQuery], [companySettingsQuery]]);
	const permissions = await queryClient.ensureQueryData(rbacPermissionsQuery("company_settings", companyId));
	const canViewHome = permissions.includes("company_settings:viewHome");
	const canViewBranding = permissions.includes("company_settings:viewBranding");
	const canViewFeatures = permissions.includes("company_settings:viewFeatures");
	const canViewSettings = permissions.includes("company_settings:viewSettings");

	if (!canViewHome) return null;

	await Promise.all([
		queryClient.ensureQueryData(adminSettingsHomeSeatUsageQuery(companyId)),
		...(canViewBranding ? [queryClient.ensureQueryData(adminSettingsHomeBrandingQuery(companyId))] : []),
		...(canViewFeatures ? [queryClient.ensureQueryData(adminSettingsHomeFunctionsQuery(companyId))] : []),
		...(canViewSettings ? [queryClient.ensureQueryData(adminSettingsHomeSettingsQuery(companyId))] : []),
	]);
	return null;
};

type OpenSection = "branding" | "features" | "settings" | null;

const ScrollToTopOnMount: React.FC = () => {
	useScrollToTopOfPageOnMount();
	return null;
};

const AdminSettingsHomePage: React.FC = () => {
	const targetCompanyId = readCompanyIdParam();
	const { data: permissions, isLoading: permissionsLoading } = useRbacPrimaryRolePermissions(
		"company_settings",
		targetCompanyId
	);
	const { data: company } = useCompanySettings();
	const seatUsage = useAdminSettingsHomeSeatUsage(targetCompanyId);
	const [openSection, setOpenSection] = useState<OpenSection>(null);

	const toggle = (section: Exclude<OpenSection, null>) => () =>
		setOpenSection((prev) => (prev === section ? null : section));

	if (permissionsLoading) return null;

	const resolvedPermissions = permissions ?? [];

	if (!resolvedPermissions.includes("company_settings:viewHome")) {
		return <ErrorPage code={403} />;
	}

	const canViewBranding = resolvedPermissions.includes("company_settings:viewBranding");
	const canEditBranding = resolvedPermissions.includes("company_settings:editBranding");
	const canViewFeatures = resolvedPermissions.includes("company_settings:viewFeatures");
	const canEditFeatures = resolvedPermissions.includes("company_settings:editFeatures");
	const canViewSettings = resolvedPermissions.includes("company_settings:viewSettings");
	const canEditSettings = resolvedPermissions.includes("company_settings:editSettings");

	return (
		<Sentry.ErrorBoundary fallback={<ErrorPage code={500} />}>
			<ScrollToTopOnMount />
			<PageTitle title="Company Settings" />
			<AdminContainer className="mx-auto w-full max-w-[1108px]">
				<div className="flex flex-col gap-8">
					<HeaderRow companyName={seatUsage.data?.companyName ?? company?.company?.name ?? "Company"} />
					<div className="flex flex-col gap-4">
						<SeatStatCards companyId={targetCompanyId} />
						{canViewBranding ? (
							<CustomBrandingSection
								isOpen={openSection === "branding"}
								onToggle={toggle("branding")}
								canEdit={canEditBranding}
								companyId={targetCompanyId}
							/>
						) : null}
						{canViewFeatures ? (
							<PerformYardFeaturesSection
								isOpen={openSection === "features"}
								onToggle={toggle("features")}
								canEdit={canEditFeatures}
								companyId={targetCompanyId}
							/>
						) : null}
						{canViewSettings ? (
							<CompanySettingsSection
								isOpen={openSection === "settings"}
								onToggle={toggle("settings")}
								canEdit={canEditSettings}
								companyId={targetCompanyId}
							/>
						) : null}
					</div>
				</div>
				<Spacer height={48} />
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default AdminSettingsHomePage;
